import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { ShoppingBag, Search, RefreshCw, Calendar, X, Clipboard, Globe, Info, Hash } from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchOrders = useCallback(async (page = 1) => {
    // If called from onClick, page might be an event object
    const pageNum = typeof page === 'number' ? page : 1;
    setLoading(true);
    setError('');
    try {
      const config = { 
        params: { page: pageNum } 
      };
      if (status) config.params.status = status;
      if (zipcode) config.params.zipcode = zipcode;
      
      const response = await api.get(`/api/admin/orders`, config);
      console.log('Orders API Response:', response.data);
      
      const resData = response.data;
      let orderList = [];
      let lastPage = 1;

      // Extract order list and pagination info
      if (Array.isArray(resData)) {
        orderList = resData;
      } else if (resData.data) {
        if (Array.isArray(resData.data)) {
          orderList = resData.data;
          lastPage = resData.last_page || resData.total_pages || 1;
        } else if (resData.data.data && Array.isArray(resData.data.data)) {
          orderList = resData.data.data;
          lastPage = resData.data.last_page || resData.data.total_pages || 1;
        } else if (resData.data.orders && Array.isArray(resData.data.orders)) {
          orderList = resData.data.orders;
          lastPage = resData.data.last_page || resData.data.total_pages || 1;
        }
      } else if (resData.orders && Array.isArray(resData.orders)) {
        orderList = resData.orders;
        lastPage = resData.last_page || resData.total_pages || 1;
      }

      setOrders(orderList);
      setTotalPages(lastPage);
      setCurrentPage(pageNum);
    } catch (err) {
      setError('Failed to fetch orders. Please check your permissions.');
    } finally {
      setLoading(false);
    }
  }, [status, zipcode]);

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchOrders(newPage);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const getStatusColor = (s) => {
    switch (s) {
      case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'close': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancel': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Order Management</h1>
          <p className="text-gray-500 text-sm">Track and manage customer orders.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="text"
              placeholder="Filter by Zipcode..."
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              className="w-full sm:w-48 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="close">Closed</option>
            <option value="cancel">Cancelled</option>
          </select>
          <button 
            onClick={fetchOrders}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[500px]">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200 text-left border-separate border-spacing-0">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Order ID</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Customer ID</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Location</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Date</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase text-right border-b border-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-6 py-4"><div className="h-4 bg-gray-100 rounded w-full"></div></td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    No orders found.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      #{order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      ID: {order.user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full border ${getStatusColor(order.status)} uppercase`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {order.zipcode || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1.5 opacity-40" />
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewDetails(order)}
                        className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {orders.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-xs text-gray-500">
              Showing {orders.length} results
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center text-sm rounded-md transition-colors ${
                          currentPage === pageNum 
                            ? 'bg-blue-600 text-white font-bold' 
                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <OrderDetailsModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        order={selectedOrder} 
      />
    </div>
  );
};

const OrderDetailsModal = ({ isOpen, onClose, order }) => {
  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            <h2 className="text-xl font-bold tracking-tight">Order Details</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Main Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">ID</label>
              <div className="flex items-center gap-2 text-gray-900 font-bold">
                <Hash className="w-3.5 h-3.5 text-blue-500" />
                #{order.id}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Order ID</label>
              <div className="text-gray-900 font-bold">
                {order.order_id || 'N/A'}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Status</label>
              <span className={`px-3 py-1 text-xs font-bold rounded-full border uppercase shadow-sm ${
                order.status === 'open' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                order.status === 'close' ? 'bg-green-100 text-green-800 border-green-200' :
                'bg-red-100 text-red-800 border-red-200'
              }`}>
                {order.status}
              </span>
            </div>
            <div className="text-right">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block text-right">Location</label>
              <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                <Globe className="w-4 h-4 text-blue-500" />
                {order.zipcode}
              </div>
            </div>
          </div>

          {/* Wallet Address */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">Wallet Address</label>
            <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg group">
              <Clipboard className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
              <code className="text-xs text-gray-600 break-all font-mono leading-relaxed">
                {order.wallet_address}
              </code>
            </div>
          </div>

          {/* Timestamps */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between text-sm px-1">
              <div className="flex items-center gap-2 text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>Created At</span>
              </div>
              <span className="font-medium text-gray-700">
                {new Date(order.created_at).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm px-1">
              <div className="flex items-center gap-2 text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>Updated At</span>
              </div>
              <span className="font-medium text-gray-700">
                {new Date(order.updated_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-white border border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Orders;
